import {
  Button,
  CloseButton,
  CondensedButton,
  Flex,
  Heading,
  IconButton,
  IconDownloadLine,
  IconTrashLine,
  Spinner,
  Table,
  Text,
  Tray,
  View,
} from "@instructure/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { ExternalService } from "../../../../../../services/ExternalService";
import downloadFile from "../../../../../../utils/Dom";
import { pollForFileChange } from "../../../../../../utils/pollForFileChange";
import {
  ERROR,
  useNotification,
} from "../../../../../CustomHooks/NotificationProvider";
import EnrichedFileDrop from "../../../../../LearningOutcomes/FileDrop/EnrichedFileDrop";
import { evidenceFileTypes } from "../../../../Plan/utils";

const MAX_FILE_SIZE_MB = 500;
const MAX_FILE_COUNT = 100;

// Filter out video and audio file types
const allowedFileTypes = evidenceFileTypes.filter(
  (type) => !type.match(/\.(mp4|mov|mp3|wav|ogg|m4a)$/)
);
/**
 * QualitativeMeasureEvidenceModal — Side tray for managing evidence files
 * for qualitative measures (at measure or question level).
 *
 * Props:
 * @param {boolean}  open             — Whether the tray is open
 * @param {Function} setOpen          — Setter to update tray open status
 * @param {string}   measureId        — The measure's ddl_id
 * @param {string}   measureName      — Display name for context
 * @param {string}   measureAssignees — Comma-separated assignee emails
 * @param {string}   userEmail — Current user's email for authorization
 * @param {Object|undefined} question      — Structured question object or null
 * @param {boolean}  isPublished      — Whether measure/question is published
 * @param {boolean}  isAssigned       — Explicit assignee status
 * @param {Array}    evidenceFiles    — Current evidence files managed by the parent
 * @param {Function} setEvidenceFiles — Setter to update parent's evidence files state
 */
const QualitativeMeasureEvidenceModal = ({
  open,
  setOpen,
  measureId,
  measureName,
  measureAssignees,
  userEmail,
  question,
  isPublished,
  isAssigned,
  evidenceFiles,
  setEvidenceFiles,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [fileState, setFileState] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Path helper — returns measure path or question path
  const evidencePath = question ? `${measureId}/${question.id}` : measureId;
  // Authorization helpers
  const canEdit = () => {
    if (isAssigned !== undefined) return isAssigned;
    if (!measureAssignees || !userEmail) return false;
    return measureAssignees
      .split(",")
      .map((email) => email.trim().toLowerCase())
      .includes(userEmail.toLowerCase());
  };

  // Fetch evidence files on tray open if parent hasn't loaded them yet
  useEffect(() => {
    async function fetchEvidenceFiles() {
      try {
        const assigneeMatch =
          isAssigned !== undefined
            ? isAssigned
            : measureAssignees && userEmail
              ? measureAssignees
                  .split(",")
                  .map((e) => e.trim().toLowerCase())
                  .includes(userEmail.toLowerCase())
              : false;

        if (!assigneeMatch && !isPublished) return;

        const path = question ? `${measureId}/${question.id}` : measureId;
        setRefreshing(true);
        const paths = await ExternalService.reviewFiles(
          "qualitativeEvidence",
          path
        );
        setEvidenceFiles(
          paths.filter(
            (file) =>
              file.file_name !== "response.html" &&
              !file.file_path.includes("/draft/")
          )
        );
      } catch {
        showNotification(t("evidence.fetchError"), ERROR);
      } finally {
        setRefreshing(false);
      }
    }
    if (open && !evidenceFiles) {
      fetchEvidenceFiles();
    }
  }, [
    open,
    evidenceFiles,
    measureId,
    question,
    isAssigned,
    isPublished,
    measureAssignees,
    userEmail,
    showNotification,
    setEvidenceFiles,
  ]);

  const handleSubmitEvidenceUpload = async () => {
    // Client-side validation — block upload when limits exceeded
    const totalSize = fileState.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_FILE_SIZE_MB * 1024 * 1024) {
      showNotification(t("evidence.fileSizeWarning"), ERROR);
      return;
    }
    if (fileState.length > MAX_FILE_COUNT) {
      showNotification(t("evidence.fileCountWarning"), ERROR);
      return;
    }

    setUploading(true);
    try {
      await ExternalService.saveFilesIndividually(
        fileState,
        "qualitativeEvidence",
        evidencePath
      );

      // Poll for updated file list
      const previousCount = evidenceFiles.length;
      const updatedFiles = await pollForFileChange(
        "qualitativeEvidence",
        evidencePath,
        previousCount
      );
      setFileState(null);
      setEvidenceFiles(
        updatedFiles.filter((file) => !file.file_path.includes("/draft/"))
      );
      showNotification(t("evidence.uploadSuccess"));
    } catch {
      showNotification(t("evidence.uploadError"), ERROR);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadAll = () => {
    try {
      evidenceFiles.forEach((file) => {
        downloadFile(file.file_url, file.file_name);
      });
    } catch {
      showNotification(t("evidence.downloadError"), ERROR);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const trayTitle = canEdit()
    ? t("evidence.manageEvidence")
    : t("evidence.viewEvidence");

  return (
    <Tray
      label={trayTitle}
      open={open}
      onDismiss={() => setOpen(false)}
      placement="end"
      size="medium"
    >
      <Flex direction="column" height="100vh">
        {/* Header */}
        <Flex.Item padding="medium">
          <Flex justifyItems="space-between" alignItems="center">
            <Flex.Item>
              <Heading level="h3">{trayTitle}</Heading>
            </Flex.Item>
            <Flex.Item>
              <CloseButton
                placement="end"
                onClick={() => setOpen(false)}
                screenReaderLabel={t("global.close")}
              />
            </Flex.Item>
          </Flex>
        </Flex.Item>

        {/* Context */}
        <Flex.Item padding="0 medium">
          {question ? (
            <View as="div">
              <Text weight="bold" size="medium">
                {question.question_text}
              </Text>
              {measureName && measureName !== question.question_text && (
                <View as="div" margin="xx-small 0 0 0">
                  <Text size="small" color="secondary">
                    {measureName}
                  </Text>
                </View>
              )}
            </View>
          ) : (
            <Text weight="bold" size="medium">
              {measureName}
            </Text>
          )}
        </Flex.Item>

        {/* Upload area — only for assignees */}
        {canEdit() && (
          <Flex.Item padding="medium">
            <EnrichedFileDrop
              fileState={fileState}
              setFileState={setFileState}
              fileDropHeading={t("evidence.uploadEvidence")}
              fileExtension={allowedFileTypes}
              multiFileSupport={true}
            />
            <View as="div" margin="small 0 0 0">
              <Button
                color="primary"
                onClick={handleSubmitEvidenceUpload}
                disabled={!fileState || fileState.length === 0 || uploading}
              >
                {uploading ? (
                  <Spinner
                    renderTitle={t("global.pleaseWait")}
                    size="x-small"
                  />
                ) : (
                  t("global.submit")
                )}
              </Button>
            </View>
          </Flex.Item>
        )}

        {/* Evidence file table */}
        <Flex.Item padding="medium" shouldGrow overflowY="auto">
          {refreshing ? (
            <Skeleton height="3rem" count={3} style={{ marginBottom: 8 }} />
          ) : !evidenceFiles || evidenceFiles.length === 0 ? (
            <Text fontStyle="italic">{t("evidence.noFilesUploaded")}</Text>
          ) : (
            <>
              <Table caption={t("evidence.manageEvidence")}>
                <Table.Head>
                  <Table.Row>
                    <Table.ColHeader id="fileName">
                      {t("global.fileName")}
                    </Table.ColHeader>
                    <Table.ColHeader id="lastModified">
                      {t("global.lastModified")}
                    </Table.ColHeader>
                    <Table.ColHeader id="actions" width="6rem">
                      {t("actions.actions")}
                    </Table.ColHeader>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {evidenceFiles.map((file) => (
                    <Table.Row key={file.file_name}>
                      <Table.Cell>
                        <Text wrap="break-word">{file.file_name}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="small">
                          {formatDate(file.last_modified)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <EvidenceActions
                          file={file}
                          canEdit={canEdit()}
                          evidencePath={evidencePath}
                          setEvidenceFiles={setEvidenceFiles}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              {/* Download All */}
              <View as="div" margin="small 0 0 0">
                <CondensedButton
                  renderIcon={IconDownloadLine}
                  onClick={handleDownloadAll}
                  disabled={refreshing || evidenceFiles.length === 0}
                >
                  {t("evidence.downloadAll")}
                </CondensedButton>
              </View>
            </>
          )}
        </Flex.Item>
      </Flex>
    </Tray>
  );
};

export default QualitativeMeasureEvidenceModal;

const EvidenceActions = ({ file, canEdit, evidencePath, setEvidenceFiles }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [deletingFile, setDeletingFile] = useState(false);

  const handleDeleteEvidence = async (fileName) => {
    setDeletingFile(fileName);
    try {
      await ExternalService.deleteFile(
        fileName,
        "qualitativeEvidence",
        evidencePath
      );
      setEvidenceFiles((prev) => prev.filter((f) => f.file_name !== fileName));
    } catch (error) {
      if (error?.status === 404) {
        // File already deleted (concurrent deletion) — remove silently
        setEvidenceFiles((prev) =>
          prev.filter((f) => f.file_name !== fileName)
        );
      } else {
        showNotification(t("evidence.deleteError"), ERROR);
      }
    } finally {
      setDeletingFile(null);
    }
  };

  return (
    <Flex justifyItems="space-around">
      <Flex.Item>
        <IconButton
          screenReaderLabel={t("global.download")}
          onClick={() => downloadFile(file.file_url, file.file_name)}
          size="small"
          withBackground={false}
          withBorder={false}
        >
          <IconDownloadLine />
        </IconButton>
      </Flex.Item>
      {canEdit && (
        <Flex.Item>
          <IconButton
            screenReaderLabel={t("evidence.delete")}
            onClick={() => handleDeleteEvidence(file.file_name)}
            size="small"
            withBackground={false}
            withBorder={false}
            disabled={deletingFile === file.file_name}
          >
            {deletingFile === file.file_name ? (
              <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
            ) : (
              <IconTrashLine />
            )}
          </IconButton>
        </Flex.Item>
      )}
    </Flex>
  );
};
